// Shared copy for new-user onboarding (web overlay + native carousel).
// Keeps the Chat vs Work story identical across platforms.

export const ONBOARDING_VARIANT = 'work_intro_v3';

export const IN_HOUSE_MODEL_NAME = 'Arya';

// Phthalo green — same ramp as the landing page and Plus chrome.
export const BRAND_GREEN = '#1F6B47';
export const BRAND_GREEN_HOVER = '#178A68';
export const BRAND_GREEN_DEEP = '#0D5C47';
export const BRAND_GREEN_TINT = '#5FB68F';

export const CHAT_MODE_COLOR = BRAND_GREEN_TINT;
export const WORK_MODE_COLOR = BRAND_GREEN;

export const MODE_CARDS = {
  chat: {
    id: 'chat',
    icon: 'comments',
    color: CHAT_MODE_COLOR,
    title: 'Chat',
    tagline: 'Instant answers',
    badge: 'Free to start',
    chips: [
      { icon: 'bolt', label: 'Seconds' },
      { icon: 'layer-group', label: 'Every model' },
      { icon: 'image', label: 'Create' },
    ],
  },
  work: {
    id: 'work',
    icon: 'briefcase',
    color: WORK_MODE_COLOR,
    title: 'Work',
    tagline: 'Finished jobs',
    badge: 'Plus only',
    chips: [
      { icon: 'laptop', label: 'Own computer' },
      { icon: 'file-export', label: 'Real files' },
      { icon: 'list-check', label: 'Multi-step' },
    ],
  },
};

export const CHAT_DEMO = {
  user: 'Write a punchy subject line for a fundraising email.',
  reply: 'Give twice the hope, half the time — today only.',
};

// Simulated Work run shown in onboarding. Mirrors real tool labels from
// constants/work.js so the demo matches what users will see later.
export const WORK_DEMO = {
  prompt: 'Compare the top 5 CRMs for a 10-person sales team and build me a comparison spreadsheet with a recommendation.',
  steps: [
    { icon: 'list-check', label: 'Planning the task', detail: '4 steps' },
    { icon: 'globe', label: 'Searching the web', detail: '12 results' },
    { icon: 'window-maximize', label: 'Reading pricing pages', detail: '5 sites' },
    { icon: 'table', label: 'Building the spreadsheet', detail: 'crm-comparison.xlsx' },
    { icon: 'file-lines', label: 'Writing the recommendation', detail: 'summary.md' },
    { icon: 'circle-check', label: 'Done — 2 files ready for review', detail: '3 min 40 sec', done: true },
  ],
};

// What a new user most wants to get done. Values match the backend's
// `interestedFeatures` vocabulary so profile updates keep working.
export const ONBOARDING_GOALS = [
  { value: 'writing', label: 'Write & edit', icon: 'pen-nib', color: '#F59E0B' },
  { value: 'research', label: 'Research', icon: 'magnifying-glass-chart', color: '#14B8A6' },
  { value: 'image_gen', label: 'Create images', icon: 'image', color: '#2D8A5E' },
  { value: 'video_gen', label: 'Create videos', icon: 'video', color: '#F43F5E' },
  { value: 'documents', label: 'Docs & decks', icon: 'file-lines', color: '#0891B2' },
  { value: 'code', label: 'Code & build', icon: 'code', color: '#06B6D4' },
  { value: 'brainstorm', label: 'Plan & brainstorm', icon: 'lightbulb', color: '#EAB308' },
  { value: 'education', label: 'Learn & study', icon: 'graduation-cap', color: '#EC4899' },
];

// Role-specific examples. `work` items are real Work prompts (launchable for
// Plus users); `chat` items are fast first prompts for the free path.
export const ROLE_PLAYBOOKS = {
  student: {
    headline: 'Built for students',
    work: [
      { icon: 'book-open', text: 'Turn 8 sources into a cited literature review', prompt: 'Research the current academic literature on my topic, read the most cited sources, and write a properly cited literature review as a DOCX with a bibliography.' },
      { icon: 'table', text: 'Build a study plan spreadsheet for finals', prompt: 'Build me a finals study plan spreadsheet: one tab per course, daily blocks for the next three weeks, and a summary tab tracking progress. Save it as XLSX.' },
      { icon: 'file-powerpoint', text: 'Create a presentation from your notes', prompt: 'Turn my notes into a clear 10-slide presentation with speaker notes and save it as a PPTX.' },
    ],
    chat: [
      'Explain this concept like I am 15, then quiz me on it',
      'Help me outline a 5-paragraph essay on a topic I pick',
      'Make me a flashcard set for my next exam',
    ],
  },
  product_management: {
    headline: 'Built for product managers',
    work: [
      { icon: 'file-lines', text: 'Draft a full PRD from a one-line idea', prompt: 'Write a complete PRD for this feature idea: problem, goals, user stories, requirements, success metrics, and open questions. Save it as a DOCX.' },
      { icon: 'magnifying-glass', text: 'Research competitors and build a comparison matrix', prompt: 'Research our top 5 competitors for this product, compare features and pricing, and build a comparison spreadsheet with a summary of gaps and opportunities.' },
      { icon: 'file-powerpoint', text: 'Turn research into a roadmap deck', prompt: 'Create a 10-slide roadmap presentation from this material for leadership, with a clear narrative and speaker notes. Save as PPTX.' },
    ],
    chat: [
      'Help me write crisp user stories for a feature',
      'Give me 10 interview questions for user research',
      'Rewrite this product update so executives will read it',
    ],
  },
  engineering: {
    headline: 'Built for engineers',
    work: [
      { icon: 'code', text: 'Scaffold a working project and run the tests', prompt: 'Scaffold a small working project for this idea, write tests, run them, fix failures, and give me the code as files with a README.' },
      { icon: 'screwdriver-wrench', text: 'Debug a stack trace against the real docs', prompt: 'Debug this error. Read the relevant library documentation online, reproduce the issue in the workspace, and give me a fix with an explanation.' },
      { icon: 'file-lines', text: 'Write an architecture doc from a codebase', prompt: 'Analyze the attached code and write an architecture document: components, data flow, tradeoffs, and risks. Save it as markdown.' },
    ],
    chat: [
      'Explain this code and suggest a cleaner version',
      'Write a regex and explain each part',
      'Compare two approaches to a design problem I describe',
    ],
  },
  human_resources: {
    headline: 'Built for HR teams',
    work: [
      { icon: 'file-lines', text: 'Write a job description and interview kit', prompt: 'Write a job description, a scorecard, and a structured interview guide for this role. Save them as DOCX files.' },
      { icon: 'table', text: 'Build an onboarding checklist spreadsheet', prompt: 'Build a 30-60-90 day onboarding checklist spreadsheet for new hires in this role with owners and due dates. Save as XLSX.' },
      { icon: 'scale-balanced', text: 'Research policy requirements across states', prompt: 'Research the leave policy requirements for the states we operate in, summarize the differences, and produce a policy comparison document with sources.' },
    ],
    chat: [
      'Draft a warm but clear offer letter',
      'Help me phrase difficult performance feedback',
      'Summarize this policy in plain language',
    ],
  },
  finance: {
    headline: 'Built for finance',
    work: [
      { icon: 'table', text: 'Build a 3-statement model from assumptions', prompt: 'Build a 3-statement financial model spreadsheet from these assumptions with a scenario tab and a summary dashboard. Save as XLSX.' },
      { icon: 'chart-line', text: 'Analyze a spreadsheet and find the story', prompt: 'Analyze the attached financial data. Find trends, anomalies, and risks, then write a review-ready memo with charts.' },
      { icon: 'magnifying-glass', text: 'Research a market and write an investment memo', prompt: 'Research this company and its market, then write an investment memo with sourced data, risks, and a recommendation. Save as DOCX.' },
    ],
    chat: [
      'Explain a financial concept with a worked example',
      'Turn these numbers into a clear executive summary',
      'Sanity-check my budget assumptions',
    ],
  },
  marketing: {
    headline: 'Built for marketers',
    work: [
      { icon: 'bullhorn', text: 'Research an audience and write a full campaign', prompt: 'Research this audience and competitor messaging, then write a complete campaign: positioning, 10 ad variations, landing page copy, and a 4-email sequence. Save as DOCX.' },
      { icon: 'table', text: 'Build a 30-day content calendar', prompt: 'Build a 30-day content calendar spreadsheet for our channels with topics, hooks, formats, and CTAs. Save as XLSX.' },
      { icon: 'image', text: 'Generate a set of on-brand visuals', prompt: 'Create six on-brand social media graphics for this campaign in different formats and save them as image files.' },
    ],
    chat: [
      'Write 5 headline variations for a landing page',
      'Give me hooks for a product launch post',
      'Rewrite this email to sound less salesy',
    ],
  },
  sales: {
    headline: 'Built for sales',
    work: [
      { icon: 'magnifying-glass', text: 'Research 20 prospects and draft personalized outreach', prompt: 'Research these 20 companies online, note recent news and likely pain points, and draft a personalized outreach email for each. Save as a spreadsheet.' },
      { icon: 'file-lines', text: 'Build a proposal from call notes', prompt: 'Turn my call notes into a polished proposal with pricing options and next steps. Save as DOCX.' },
      { icon: 'file-powerpoint', text: 'Create a pitch deck for a specific buyer', prompt: 'Create a 10-slide pitch deck tailored to this buyer with a clear story and speaker notes. Save as PPTX.' },
    ],
    chat: [
      'Write a follow-up email after a discovery call',
      'Help me handle a pricing objection',
      'Summarize this prospect\'s website in 5 bullets',
    ],
  },
  operations: {
    headline: 'Built for operations',
    work: [
      { icon: 'list-check', text: 'Document a process as a full SOP', prompt: 'Turn this rough process description into a complete SOP with steps, owners, checklists, and failure modes. Save as DOCX.' },
      { icon: 'table', text: 'Build a vendor comparison workbook', prompt: 'Research vendors for this need, compare pricing and features, and build a decision workbook with a recommendation. Save as XLSX.' },
      { icon: 'chart-line', text: 'Analyze operational data for bottlenecks', prompt: 'Analyze the attached data, identify bottlenecks and trends, and write a summary with charts and recommendations.' },
    ],
    chat: [
      'Draft a clear announcement about a process change',
      'Help me prioritize this list of tasks',
      'Turn these notes into a checklist',
    ],
  },
  data_science: {
    headline: 'Built for data scientists',
    work: [
      { icon: 'chart-pie', text: 'Explore a dataset and produce a report', prompt: 'Run an exploratory analysis on the attached dataset: distributions, correlations, outliers, and key findings. Produce a report with charts and the code you used.' },
      { icon: 'code', text: 'Build and evaluate a baseline model', prompt: 'Build a baseline model for this problem, evaluate it, and give me the code, metrics, and a short write-up of next steps.' },
      { icon: 'file-lines', text: 'Summarize recent papers on a topic', prompt: 'Find and read recent papers on this topic, then write a structured summary of methods and results with citations.' },
    ],
    chat: [
      'Explain a statistical test and when to use it',
      'Help me write a SQL query I describe in words',
      'Review my analysis plan for gaps',
    ],
  },
  design: {
    headline: 'Built for designers',
    work: [
      { icon: 'image', text: 'Generate a full moodboard from a brief', prompt: 'Create a moodboard for this brief: 8 generated images in a consistent style, a palette, and a short rationale. Save the images and a summary document.' },
      { icon: 'file-powerpoint', text: 'Build a design review deck', prompt: 'Turn these design notes and screenshots into a polished review deck with a clear narrative. Save as PPTX.' },
      { icon: 'magnifying-glass', text: 'Research design patterns across competitors', prompt: 'Research how leading products handle this UX pattern, capture examples, and write a comparison with recommendations.' },
    ],
    chat: [
      'Give me 10 name ideas and taglines for a concept',
      'Critique this design description like a senior designer',
      'Generate an image in a style I describe',
    ],
  },
  legal: {
    headline: 'Built for legal',
    work: [
      { icon: 'file-lines', text: 'Review a contract and produce an issues list', prompt: 'Review the attached contract, flag risky clauses, and produce an issues list with suggested redlines. Save as DOCX.' },
      { icon: 'magnifying-glass', text: 'Research a legal question with sources', prompt: 'Research this legal question across authoritative sources, summarize the state of the law, and write a memo with citations.' },
      { icon: 'table', text: 'Build a compliance tracker', prompt: 'Build a compliance obligations tracker spreadsheet for our situation with deadlines, owners, and status. Save as XLSX.' },
    ],
    chat: [
      'Explain this clause in plain English',
      'Draft a polite but firm demand letter',
      'Summarize the key terms of this agreement',
    ],
  },
  homemaker: {
    headline: 'Built for running a home',
    work: [
      { icon: 'cart-shopping', text: 'Plan a month of dinners with a grocery list', prompt: 'Plan four weeks of family dinners around our preferences and budget, then build a weekly grocery list spreadsheet organized by store section. Save as XLSX.' },
      { icon: 'calendar-days', text: 'Build a family schedule and chore chart', prompt: 'Build a printable weekly family schedule and rotating chore chart for our household as a nicely formatted DOCX.' },
      { icon: 'chart-pie', text: 'Create a household budget tracker', prompt: 'Create a household budget spreadsheet with monthly categories, a savings goal tracker, and a summary dashboard. Save as XLSX.' },
    ],
    chat: [
      'Give me a quick dinner idea with what is in my fridge',
      'Help me plan a birthday party on a budget',
      'Write a kind note to my child\'s teacher',
    ],
  },
  ministry: {
    headline: 'Built for pastors and ministry leaders',
    work: [
      { icon: 'book-open', text: 'Research a passage and draft a sermon outline', prompt: 'Study this passage using trusted commentaries and cross references, then write a full sermon outline with illustrations and application points. Save as DOCX.' },
      { icon: 'file-powerpoint', text: 'Build a small-group study guide and slides', prompt: 'Create a six-week small-group Bible study guide with discussion questions, plus a slide deck for each week. Save as DOCX and PPTX.' },
      { icon: 'newspaper', text: 'Write this week\'s church newsletter', prompt: 'Draft our weekly church newsletter from these announcements: warm pastoral intro, events, prayer requests, and a closing encouragement. Save as DOCX.' },
    ],
    chat: [
      'Help me find scripture on hope for a grieving family',
      'Write a short devotional for our youth group',
      'Draft a warm welcome message for first-time visitors',
    ],
  },
  small_business: {
    headline: 'Built for small business owners',
    work: [
      { icon: 'magnifying-glass', text: 'Research local competitors and pricing', prompt: 'Research my local competitors, compare their offerings and pricing, and build a spreadsheet with a summary of where I can stand out. Save as XLSX.' },
      { icon: 'pen-to-square', text: 'Write a month of social media posts', prompt: 'Write a month of social media posts for my business with a posting calendar, captions, and image ideas. Save as DOCX.' },
      { icon: 'table', text: 'Build a cash-flow forecast', prompt: 'Build a 12-month cash-flow forecast spreadsheet for my business with revenue, expenses, and a break-even summary. Save as XLSX.' },
    ],
    chat: [
      'Write a reply to a negative customer review',
      'Help me price a new service',
      'Draft a job posting for my first hire',
    ],
  },
  healthcare: {
    headline: 'Built for healthcare professionals',
    work: [
      { icon: 'magnifying-glass', text: 'Summarize the latest research on a condition', prompt: 'Research the most recent peer-reviewed literature on this condition, summarize key findings and guideline changes, and write a cited brief. Save as DOCX.' },
      { icon: 'file-lines', text: 'Create patient-friendly education handouts', prompt: 'Write clear, patient-friendly education handouts on this topic at an 8th-grade reading level, with a FAQ section. Save as DOCX.' },
      { icon: 'file-powerpoint', text: 'Build an in-service training deck', prompt: 'Create a 12-slide in-service training presentation for staff on this topic with speaker notes. Save as PPTX.' },
    ],
    chat: [
      'Explain this lab result in plain language',
      'Help me draft a professional note to a colleague',
      'Quiz me on a clinical topic',
    ],
  },
  education: {
    headline: 'Built for teachers',
    work: [
      { icon: 'list-check', text: 'Build a full unit plan with lessons and quizzes', prompt: 'Build a two-week unit plan for this topic and grade level: daily lesson plans, activities, a quiz with answer key, and a rubric. Save as DOCX.' },
      { icon: 'file-powerpoint', text: 'Turn a lesson into classroom slides', prompt: 'Turn this lesson into an engaging classroom slide deck with discussion prompts and a warm-up. Save as PPTX.' },
      { icon: 'table', text: 'Create a differentiated worksheet set', prompt: 'Create three versions of a worksheet on this topic for below, on, and above grade level, with answer keys. Save as DOCX.' },
    ],
    chat: [
      'Give me five ways to explain this concept',
      'Write a positive note home to a parent',
      'Help me plan a 45-minute lesson',
    ],
  },
  trades: {
    headline: 'Built for the trades',
    work: [
      { icon: 'table', text: 'Build a job estimate and materials list', prompt: 'Build a professional job estimate spreadsheet for this project: materials with current prices, labor hours, markup, and a client-ready total. Save as XLSX.' },
      { icon: 'file-lines', text: 'Draft a contract and scope of work', prompt: 'Write a clear residential contract and scope-of-work document for this job, including payment schedule and change-order terms. Save as DOCX.' },
      { icon: 'pen-to-square', text: 'Set up a simple marketing plan for referrals', prompt: 'Create a simple local marketing plan for my trade business: Google profile, review requests, and a month of posts. Save as DOCX.' },
    ],
    chat: [
      'Write a text to a customer about a schedule change',
      'Help me price a small job',
      'Explain a code requirement in plain English',
    ],
  },
  agriculture: {
    headline: 'Built for farmers and ranchers',
    work: [
      { icon: 'calendar-days', text: 'Build a planting and harvest calendar', prompt: 'Build a planting, care, and harvest calendar for my crops and region with reminders and notes. Save as XLSX.' },
      { icon: 'magnifying-glass', text: 'Research grants and programs I qualify for', prompt: 'Research agricultural grants, cost-share, and USDA programs my operation may qualify for, with deadlines and requirements in a spreadsheet.' },
      { icon: 'table', text: 'Create a farm budget and input tracker', prompt: 'Create a farm operating budget spreadsheet with inputs, equipment, labor, and expected yields. Save as XLSX.' },
    ],
    chat: [
      'Help me diagnose a plant or livestock issue',
      'Write a listing to sell at the farmers market',
      'Explain soil test results',
    ],
  },
  creator: {
    headline: 'Built for creators and writers',
    work: [
      { icon: 'pen-to-square', text: 'Draft a full article or chapter from an outline', prompt: 'Write a polished long-form piece from this outline in my voice, with a strong hook and clean structure. Save as DOCX.' },
      { icon: 'image', text: 'Create a set of images for a project', prompt: 'Generate a cohesive set of eight images in a consistent style for this project, then deliver them in a folder.' },
      { icon: 'calendar-days', text: 'Build a 30-day content calendar', prompt: 'Build a 30-day content calendar for my channels with hooks, formats, and posting times. Save as XLSX.' },
    ],
    chat: [
      'Give me 10 headline options for this piece',
      'Rewrite this paragraph to be punchier',
      'Create an image from my description',
    ],
  },
  real_estate: {
    headline: 'Built for real estate',
    work: [
      { icon: 'magnifying-glass', text: 'Research a neighborhood and build a market report', prompt: 'Research this neighborhood: recent sales, trends, schools, and amenities, and write a client-ready market report. Save as DOCX.' },
      { icon: 'pen-to-square', text: 'Write listing descriptions and social posts', prompt: 'Write compelling listing descriptions for these properties plus matching social posts and an email blast. Save as DOCX.' },
      { icon: 'table', text: 'Build a comparable sales analysis', prompt: 'Build a comparable sales analysis spreadsheet for this property with adjustments and a suggested price range. Save as XLSX.' },
    ],
    chat: [
      'Write a follow-up text to a new lead',
      'Explain closing costs to a first-time buyer',
      'Draft an open house announcement',
    ],
  },
  retired: {
    headline: 'Built for making the most of your time',
    work: [
      { icon: 'magnifying-glass', text: 'Plan a trip with a full itinerary', prompt: 'Plan a trip for me: research destinations, build a day-by-day itinerary with costs, and save it as a DOCX I can print.' },
      { icon: 'book-open', text: 'Research family history and write it up', prompt: 'Research my family history from the details I share, organize what you find, and write a family history document with sources. Save as DOCX.' },
      { icon: 'chart-pie', text: 'Organize a retirement budget', prompt: 'Build a retirement budget spreadsheet with income sources, monthly expenses, and a simple projection. Save as XLSX.' },
    ],
    chat: [
      'Explain a health or Medicare question simply',
      'Suggest a good book based on what I like',
      'Help me write a letter to my grandchildren',
    ],
  },
  other: {
    headline: 'Built for whatever you are working on',
    work: [
      { icon: 'magnifying-glass', text: 'Research a topic and write a sourced brief', prompt: 'Research this topic thoroughly, read the best sources, and write a polished brief with citations. Save as DOCX.' },
      { icon: 'table', text: 'Compare options in a decision spreadsheet', prompt: 'Compare the options for this decision in a spreadsheet: criteria, scores, risks, and a recommendation. Save as XLSX.' },
      { icon: 'file-powerpoint', text: 'Turn notes into a finished presentation', prompt: 'Turn these notes into a 10-slide presentation with a clear story and speaker notes. Save as PPTX.' },
    ],
    chat: [
      'Help me plan my week and prioritize what matters',
      'Explain something I have always wondered about',
      'Create an image of something I describe',
    ],
  },
};

export function getRolePlaybook(role) {
  return ROLE_PLAYBOOKS[role] || ROLE_PLAYBOOKS.other;
}

// Short flavor used to personalize the Chat vs Work demo cards from the
// previous "about you" step. Prompts stay one-line so they fit the mock UI.
const DEFAULT_DEMO_FLAVOR = {
  thing: 'this project',
  topic: 'the options in front of me',
  image: 'a clean, modern workspace at golden hour',
  video: 'a 15-second explainer for this idea',
  file: 'brief.docx',
  sheet: 'comparison.xlsx',
  deck: 'overview.pptx',
  hook: 'Start here. Finish stronger.',
  searchLabel: 'Searching the web',
  readLabel: 'Reading the best sources',
  buildLabel: 'Building the spreadsheet',
  writeLabel: 'Writing the recommendation',
};

const ROLE_DEMO_FLAVORS = {
  student: {
    thing: 'my term paper',
    topic: 'the Industrial Revolution',
    image: 'a quiet library table stacked with open books',
    video: 'a 30-second recap of this week\'s lecture',
    file: 'lit-review.docx',
    sheet: 'study-plan.xlsx',
    deck: 'class-presentation.pptx',
    hook: 'The war did not start with a shot. It started with a silence nobody broke.',
    searchLabel: 'Searching academic papers',
    readLabel: 'Reading cited sources',
    buildLabel: 'Building the study plan',
    writeLabel: 'Writing the literature review',
  },
  engineering: {
    thing: 'this feature',
    topic: 'the tradeoffs in this design',
    image: 'a dark IDE with a clean architecture diagram beside it',
    video: 'a 20-second demo of this bug fix',
    file: 'architecture.md',
    sheet: 'perf-benchmarks.xlsx',
    deck: 'design-review.pptx',
    hook: 'Name it what it does. The rest of the code will follow.',
    searchLabel: 'Reading the docs',
    readLabel: 'Checking library issues',
    buildLabel: 'Writing the tests',
    writeLabel: 'Drafting the README',
  },
  small_business: {
    thing: 'this month\'s offer',
    topic: 'my local competitors',
    image: 'a storefront at opening time with the lights just on',
    video: 'a 15-second promo for this weekend\'s sale',
    file: 'offer-copy.docx',
    sheet: 'competitor-map.xlsx',
    deck: 'owner-update.pptx',
    hook: 'Same quality. Half the wait. This week only.',
    searchLabel: 'Researching local competitors',
    readLabel: 'Reading their pricing pages',
    buildLabel: 'Building the comparison',
    writeLabel: 'Writing where we stand out',
  },
  homemaker: {
    thing: 'this week\'s dinners',
    topic: 'easy meals from what we have',
    image: 'a warm kitchen table set for family dinner',
    video: 'a 20-second walkthrough of tonight\'s recipe',
    file: 'meal-plan.docx',
    sheet: 'grocery-list.xlsx',
    deck: 'family-week.pptx',
    hook: 'Twenty minutes, one pan, and nobody asks what is for dinner.',
    searchLabel: 'Finding recipes that fit',
    readLabel: 'Checking what is in season',
    buildLabel: 'Building the grocery list',
    writeLabel: 'Writing the weekly plan',
  },
  ministry: {
    thing: 'this Sunday\'s sermon',
    topic: 'hope in suffering',
    image: 'a quiet sanctuary at sunrise',
    video: 'a 30-second invitation to this Sunday\'s service',
    file: 'sermon-outline.docx',
    sheet: 'series-plan.xlsx',
    deck: 'sunday-slides.pptx',
    hook: 'Hope is not a feeling we wait for — it is a Person we follow.',
    searchLabel: 'Searching commentaries',
    readLabel: 'Reading the passage',
    buildLabel: 'Outlining the sermon',
    writeLabel: 'Writing the manuscript',
  },
  education: {
    thing: 'this week\'s lesson',
    topic: 'fractions for 5th grade',
    image: 'a bright classroom whiteboard with a clear diagram',
    video: 'a 30-second hook for tomorrow\'s lesson',
    file: 'unit-plan.docx',
    sheet: 'rubric.xlsx',
    deck: 'lesson-slides.pptx',
    hook: 'If they can explain it to a friend, they understand it.',
    searchLabel: 'Finding teaching ideas',
    readLabel: 'Reading the standards',
    buildLabel: 'Building the worksheet',
    writeLabel: 'Writing the lesson plan',
  },
  creator: {
    thing: 'this next piece',
    topic: 'headlines that actually get opened',
    image: 'a writer\'s desk with a notebook and morning light',
    video: 'a 15-second teaser for this essay',
    file: 'draft.docx',
    sheet: 'content-calendar.xlsx',
    deck: 'pitch-deck.pptx',
    hook: 'You do not need more time. You need a sentence they cannot scroll past.',
    searchLabel: 'Researching the angle',
    readLabel: 'Reading what is working',
    buildLabel: 'Building the calendar',
    writeLabel: 'Writing the draft',
  },
  healthcare: {
    thing: 'this patient handout',
    topic: 'the latest guideline change',
    image: 'a calm clinic hallway with morning light',
    video: 'a 30-second explainer patients can follow',
    file: 'patient-handout.docx',
    sheet: 'guideline-compare.xlsx',
    deck: 'inservice.pptx',
    hook: 'Here is what changed, why it matters, and what to do Monday morning.',
    searchLabel: 'Searching the literature',
    readLabel: 'Reading recent studies',
    buildLabel: 'Building the comparison',
    writeLabel: 'Writing the brief',
  },
  product_management: {
    thing: 'this PRD',
    topic: 'our top 5 competitors',
    image: 'a product whiteboard with a simple user journey',
    video: 'a 20-second walkthrough of this feature',
    file: 'prd.docx',
    sheet: 'competitor-matrix.xlsx',
    deck: 'roadmap.pptx',
    hook: 'The user does not want a feature. They want Tuesday to be easier.',
    searchLabel: 'Researching competitors',
    readLabel: 'Reading their feature pages',
    buildLabel: 'Building the matrix',
    writeLabel: 'Writing the PRD',
  },
  human_resources: {
    thing: 'this job description',
    topic: 'what great candidates actually want',
    image: 'a bright office welcome desk on a new hire\'s first day',
    video: 'a 20-second welcome for new hires',
    file: 'job-description.docx',
    sheet: 'onboarding-checklist.xlsx',
    deck: 'interview-kit.pptx',
    hook: 'Come do the work you are already good at — with people who notice.',
    searchLabel: 'Researching the role',
    readLabel: 'Reading comparable listings',
    buildLabel: 'Building the scorecard',
    writeLabel: 'Writing the job post',
  },
  finance: {
    thing: 'this quarter\'s update',
    topic: 'where the budget actually went',
    image: 'a clean desk with a ledger and a cup of coffee',
    video: 'a 20-second summary of this quarter',
    file: 'board-memo.docx',
    sheet: 'budget-vs-actual.xlsx',
    deck: 'cfo-update.pptx',
    hook: 'The story is not the variance. It is what we do before the next one.',
    searchLabel: 'Pulling the latest figures',
    readLabel: 'Reading last quarter\'s notes',
    buildLabel: 'Building the model',
    writeLabel: 'Writing the memo',
  },
  marketing: {
    thing: 'this campaign brief',
    topic: 'what our audience clicked last month',
    image: 'a campaign moodboard pinned above a laptop',
    video: 'a 15-second ad for this launch',
    file: 'campaign-brief.docx',
    sheet: 'channel-plan.xlsx',
    deck: 'launch-deck.pptx',
    hook: 'Give twice the hope, half the time — today only.',
    searchLabel: 'Researching the audience',
    readLabel: 'Reading last campaign results',
    buildLabel: 'Building the calendar',
    writeLabel: 'Writing the brief',
  },
  sales: {
    thing: 'this outreach sequence',
    topic: 'why this team should switch',
    image: 'a notebook with a short list of names and next steps',
    video: 'a 20-second product walkthrough for a prospect',
    file: 'outreach.docx',
    sheet: 'crm-comparison.xlsx',
    deck: 'pitch.pptx',
    hook: 'You are not behind. You are one conversation from catching up.',
    searchLabel: 'Researching the account',
    readLabel: 'Reading their site',
    buildLabel: 'Building the comparison',
    writeLabel: 'Writing the sequence',
  },
  operations: {
    thing: 'this process doc',
    topic: 'where handoffs are breaking',
    image: 'a clean flowchart on a workshop wall',
    video: 'a 20-second walkthrough of the new process',
    file: 'sop.docx',
    sheet: 'ops-tracker.xlsx',
    deck: 'ops-review.pptx',
    hook: 'If it takes a meeting to explain, it is not a process yet.',
    searchLabel: 'Mapping the current steps',
    readLabel: 'Reading existing SOPs',
    buildLabel: 'Building the tracker',
    writeLabel: 'Writing the playbook',
  },
  data_science: {
    thing: 'this analysis',
    topic: 'what is actually driving the metric',
    image: 'a laptop with a simple, honest chart',
    video: 'a 20-second explainer of this finding',
    file: 'analysis-brief.docx',
    sheet: 'findings.xlsx',
    deck: 'insights.pptx',
    hook: 'The number is not the insight. The sentence under it is.',
    searchLabel: 'Pulling the datasets',
    readLabel: 'Checking the methodology',
    buildLabel: 'Building the model',
    writeLabel: 'Writing the findings',
  },
  design: {
    thing: 'this design brief',
    topic: 'how leading products handle this pattern',
    image: 'a moodboard of type, color, and one strong photograph',
    video: 'a 15-second motion study for this screen',
    file: 'design-rationale.docx',
    sheet: 'pattern-compare.xlsx',
    deck: 'design-review.pptx',
    hook: 'Make the next tap obvious. Everything else is decoration.',
    searchLabel: 'Gathering visual references',
    readLabel: 'Reviewing competitor patterns',
    buildLabel: 'Building the moodboard',
    writeLabel: 'Writing the rationale',
  },
  legal: {
    thing: 'this issues list',
    topic: 'the risk in this clause',
    image: 'a quiet office with a marked-up contract',
    video: 'a 20-second explainer of this change',
    file: 'issues-list.docx',
    sheet: 'compliance-tracker.xlsx',
    deck: 'counsel-brief.pptx',
    hook: 'The risk is not the sentence. It is who it is silent about.',
    searchLabel: 'Searching authorities',
    readLabel: 'Reading the agreement',
    buildLabel: 'Building the tracker',
    writeLabel: 'Writing the memo',
  },
  trades: {
    thing: 'this job estimate',
    topic: 'what this renovation should cost',
    image: 'a job site at first light with tools laid out',
    video: 'a 20-second walkthrough of the scope',
    file: 'scope-of-work.docx',
    sheet: 'job-estimate.xlsx',
    deck: 'client-proposal.pptx',
    hook: 'Clear price. Clear timeline. No surprises after we start.',
    searchLabel: 'Checking material prices',
    readLabel: 'Reading the scope',
    buildLabel: 'Building the estimate',
    writeLabel: 'Writing the proposal',
  },
  agriculture: {
    thing: 'this season\'s plan',
    topic: 'grants my operation may qualify for',
    image: 'early light over a field just after planting',
    video: 'a 20-second update from the farm this week',
    file: 'season-plan.docx',
    sheet: 'planting-calendar.xlsx',
    deck: 'operation-review.pptx',
    hook: 'Plant for the season you have, not the one you were promised.',
    searchLabel: 'Researching programs',
    readLabel: 'Reading eligibility rules',
    buildLabel: 'Building the calendar',
    writeLabel: 'Writing the plan',
  },
  real_estate: {
    thing: 'this listing',
    topic: 'what sold on this street last quarter',
    image: 'a sunlit living room staged for an open house',
    video: 'a 30-second walkthrough of this home',
    file: 'listing.docx',
    sheet: 'comps.xlsx',
    deck: 'buyer-presentation.pptx',
    hook: 'Come see the light at 4 p.m. That is when this house makes the case.',
    searchLabel: 'Researching recent sales',
    readLabel: 'Reading the comps',
    buildLabel: 'Building the CMA',
    writeLabel: 'Writing the listing',
  },
  retired: {
    thing: 'this trip',
    topic: 'the family story I want written down',
    image: 'a kitchen table with old photographs and a notebook',
    video: 'a 30-second invitation to the family reunion',
    file: 'itinerary.docx',
    sheet: 'trip-budget.xlsx',
    deck: 'family-history.pptx',
    hook: 'We have time now. Let us spend it on something worth keeping.',
    searchLabel: 'Researching destinations',
    readLabel: 'Reading traveler notes',
    buildLabel: 'Building the itinerary',
    writeLabel: 'Writing it up',
  },
  other: {
    thing: 'this project',
    topic: 'the decision I need to make',
    image: 'a clean desk with one notebook and a good lamp',
    video: 'a 15-second explainer for this idea',
    file: 'brief.docx',
    sheet: 'options.xlsx',
    deck: 'overview.pptx',
    hook: 'Write the decision in one sentence. Then the work gets obvious.',
    searchLabel: 'Searching the web',
    readLabel: 'Reading the best sources',
    buildLabel: 'Building the comparison',
    writeLabel: 'Writing the brief',
  },
};

const ROLE_DEFAULT_GOAL = {
  student: 'education',
  engineering: 'code',
  small_business: 'research',
  homemaker: 'brainstorm',
  ministry: 'writing',
  education: 'education',
  creator: 'writing',
  healthcare: 'research',
  product_management: 'documents',
  human_resources: 'documents',
  finance: 'research',
  marketing: 'writing',
  sales: 'research',
  operations: 'documents',
  data_science: 'research',
  design: 'image_gen',
  legal: 'research',
  trades: 'documents',
  agriculture: 'research',
  real_estate: 'writing',
  retired: 'research',
  other: 'research',
};

const GOAL_CHAT_REPLIES = {
  research: 'Three things actually change the decision. The rest is noise.',
  image_gen: 'Done — warm light, clean composition, ready to download.',
  video_gen: 'Hook, one beat, one ask. Fifteen seconds. Cut on the ask.',
  documents: 'One page: headline, three points, one next step.',
  code: 'Same behavior, half the branches. Name it for what it does.',
  brainstorm: 'Eight options. Star the two you would actually do this week.',
  education: 'If you can say it in one sentence, you understand it. Quiz next.',
};

function buildWorkSteps(goal, flavor) {
  const plan = { icon: 'list-check', label: 'Planning the task', detail: '4 steps' };
  const done = { icon: 'circle-check', label: 'Done — 2 files ready for review', detail: '3 min 40 sec', done: true };

  if (goal === 'image_gen') {
    return [
      plan,
      { icon: 'globe', label: 'Gathering visual references', detail: '18 images' },
      { icon: 'image', label: 'Generating the set', detail: '8 images' },
      { icon: 'palette', label: 'Matching the style', detail: 'palette.md' },
      { icon: 'file-lines', label: 'Writing the rationale', detail: flavor.file },
      done,
    ];
  }
  if (goal === 'video_gen') {
    return [
      plan,
      { icon: 'file-lines', label: 'Writing the script', detail: 'script.md' },
      { icon: 'image', label: 'Storyboarding scenes', detail: '6 frames' },
      { icon: 'video', label: 'Generating the clip', detail: '15 sec' },
      { icon: 'file-export', label: 'Packaging the files', detail: flavor.file },
      done,
    ];
  }
  if (goal === 'code') {
    return [
      plan,
      { icon: 'globe', label: 'Reading the docs', detail: '4 pages' },
      { icon: 'code', label: 'Writing the project', detail: '12 files' },
      { icon: 'screwdriver-wrench', label: 'Running the tests', detail: 'all passing' },
      { icon: 'file-lines', label: 'Writing the README', detail: 'README.md' },
      { icon: 'circle-check', label: 'Done — project ready to run', detail: '4 min 10 sec', done: true },
    ];
  }
  if (goal === 'education') {
    return [
      plan,
      { icon: 'globe', label: flavor.searchLabel, detail: '9 sources' },
      { icon: 'book-open', label: 'Reading the material', detail: '5 sources' },
      { icon: 'list-check', label: 'Building the study guide', detail: flavor.file },
      { icon: 'table', label: 'Writing the quiz', detail: 'quiz.docx' },
      done,
    ];
  }
  if (goal === 'brainstorm') {
    return [
      plan,
      { icon: 'lightbulb', label: 'Listing the options', detail: '12 ideas' },
      { icon: 'globe', label: flavor.searchLabel, detail: '8 results' },
      { icon: 'table', label: 'Scoring the options', detail: flavor.sheet },
      { icon: 'file-lines', label: 'Writing the plan', detail: flavor.file },
      done,
    ];
  }
  if (goal === 'documents') {
    return [
      plan,
      { icon: 'globe', label: flavor.searchLabel, detail: '10 results' },
      { icon: 'window-maximize', label: flavor.readLabel, detail: '4 sources' },
      { icon: 'file-lines', label: 'Writing the document', detail: flavor.file },
      { icon: 'file-powerpoint', label: 'Building the deck', detail: flavor.deck },
      done,
    ];
  }
  if (goal === 'writing') {
    return [
      plan,
      { icon: 'globe', label: flavor.searchLabel, detail: '11 results' },
      { icon: 'window-maximize', label: flavor.readLabel, detail: '6 sources' },
      { icon: 'list-check', label: 'Outlining the draft', detail: 'outline.md' },
      { icon: 'file-lines', label: 'Writing the document', detail: flavor.file },
      done,
    ];
  }
  return [
    plan,
    { icon: 'globe', label: flavor.searchLabel, detail: '12 results' },
    { icon: 'window-maximize', label: flavor.readLabel, detail: '5 sites' },
    { icon: 'table', label: flavor.buildLabel, detail: flavor.sheet },
    { icon: 'file-lines', label: flavor.writeLabel, detail: flavor.file },
    done,
  ];
}

function buildChatAndWork(goal, flavor) {
  const chatByGoal = {
    writing: { user: `Write a punchy opening for ${flavor.thing}.`, reply: flavor.hook },
    research: { user: `Give me the 3 most important facts about ${flavor.topic}.`, reply: GOAL_CHAT_REPLIES.research },
    image_gen: { user: `Create an image of ${flavor.image}.`, reply: GOAL_CHAT_REPLIES.image_gen },
    video_gen: { user: `Write a 15-second script for ${flavor.video}.`, reply: GOAL_CHAT_REPLIES.video_gen },
    documents: { user: `Outline a one-page brief on ${flavor.thing}.`, reply: GOAL_CHAT_REPLIES.documents },
    code: { user: `Explain this and suggest a cleaner version I can use for ${flavor.thing}.`, reply: GOAL_CHAT_REPLIES.code },
    brainstorm: { user: `Give me 8 ideas for ${flavor.thing}.`, reply: GOAL_CHAT_REPLIES.brainstorm },
    education: { user: `Explain ${flavor.topic} like I am new to it, then quiz me.`, reply: GOAL_CHAT_REPLIES.education },
  };
  const workByGoal = {
    writing: `Research and write ${flavor.thing} as a finished document I can hand off today.`,
    research: `Research ${flavor.topic} and build a comparison spreadsheet with a clear recommendation.`,
    image_gen: `Generate a cohesive set of images for ${flavor.thing} and save them with a short rationale.`,
    video_gen: `Storyboard and produce a short video for ${flavor.video}, then package the script and files.`,
    documents: `Turn my notes on ${flavor.thing} into a finished document and slide deck.`,
    code: `Scaffold a small working project for ${flavor.thing}, write tests, and give me the files with a README.`,
    brainstorm: `Brainstorm options for ${flavor.thing}, score them, and write the plan I should follow.`,
    education: `Build a study guide and quiz for ${flavor.topic} I can actually use.`,
  };

  return {
    chat: chatByGoal[goal] || CHAT_DEMO,
    work: {
      prompt: workByGoal[goal] || WORK_DEMO.prompt,
      steps: buildWorkSteps(goal, flavor),
    },
  };
}

/**
 * Chat + Work mock examples for the modes step. Uses the first selected goal
 * when present (what they want to get done), flavored by role (what they do).
 * Role alone uses that role's typical job. Neither selected keeps the defaults.
 */
const ONBOARDING_GOAL_VALUES = new Set(ONBOARDING_GOALS.map((goal) => goal.value));

export function getOnboardingDemos(role, goals = []) {
  const goalKey = Array.isArray(goals) ? goals.find((value) => ONBOARDING_GOAL_VALUES.has(value)) : null;
  const flavor = (role && ROLE_DEMO_FLAVORS[role]) || DEFAULT_DEMO_FLAVOR;
  const resolvedGoal = goalKey || (role ? ROLE_DEFAULT_GOAL[role] : null);

  if (!resolvedGoal) {
    return { chat: CHAT_DEMO, work: WORK_DEMO };
  }
  return buildChatAndWork(resolvedGoal, flavor);
}
